import { useMemo } from "react";
import { useFleetServiceV1GetFleetsQuery } from "_services/modules/transport-resource/fleet";
import { dateTimeFormatter } from "_helper/timeFormatter";
import { FleetRow } from "../types";
import { TableSortState } from "components/table";

const mapFleetDraftStatus = (status?: string): FleetRow["status"] => {
  if (
    status === "Available" ||
    status === "Assigned" ||
    status === "Maintenance" ||
    status === "Inactive" ||
    status === "Draft"
  ) {
    return status;
  }
  return "Draft";
};

export const useFleetQuery = (
  sortState: TableSortState,
  currentPage: number,
  rowsPerPage: number,
  searchValue: string,
  vehicleTypeFilter: string,
  vehicleModelFilter: string,
  vendorFilter?: string,
) => {

  const sortByMap: Record<string, string> = {
    plateNumber: "plate_number",
    status: "status",
    createdAt: "created_at",
    updatedAt: "updated_at",
    hullNumber: "hull_number",
    vehicleType: "vehicle_type_id",
    vehicleModel: "vehicle_model_id",
    odometer: "odometer",
    stnkExpiry: "stnk_expiry_date",
    kirExpiry: "kir_expiry_date",
    lastMaintenance: "last_maintenance_date",
  };

  const { data: apiResponse, isLoading, isFetching } = useFleetServiceV1GetFleetsQuery({
    page: currentPage,
    limit: rowsPerPage,
    search: searchValue,
    status: "Draft",
    vehicleTypeId: vehicleTypeFilter,
    vehicleModelId: vehicleModelFilter,
    vendorId: vendorFilter || undefined,
    sortBy: sortState ? sortByMap[sortState.columnId] : undefined,
    order: sortState?.direction,
  });

  const tableData: FleetRow[] = useMemo(() => {
    if (!apiResponse?.fleet) return [];
    return apiResponse.fleet.map((item: any) => {
      const createdByName = item.created_by || "-";
      const formattedCreatedAt = item.created_at ? dateTimeFormatter(item.created_at) : "-";
      const createdAt = formattedCreatedAt === "—" ? "-" : formattedCreatedAt;

      const rawUpdatedByName = item.updated_by || (createdByName !== "-" ? createdByName : undefined);
      const rawUpdatedAt = item.updated_at || item.created_at;
      const formattedUpdatedAt = rawUpdatedAt ? dateTimeFormatter(rawUpdatedAt) : "-";
      const updatedAt = formattedUpdatedAt === "—" ? "-" : formattedUpdatedAt;
      const updatedBy = rawUpdatedByName && rawUpdatedByName.trim() !== "" ? rawUpdatedByName : "-";

      return {
        id: item.id,
        fleetPicture: item.photo_url || "",
        plateNumber: item.plate_number,
        fleetCode: item.fleet_code || "",
        vehicleType: item.vehicle_type,
        vehicleModel: item.vehicle_model,
        odometer: item.odometer,
        status: mapFleetDraftStatus(item.status),
        stnkExpiry: item.stnk_expiry,
        kirExpiry: item.kir_expiry,
        lastMaintenance: item.last_maintenance_date,
        createdBy: createdByName,
        createdAt: createdAt,
        updatedBy: updatedBy,
        updatedAt: updatedAt,
      };
    });
  }, [apiResponse]);

  return {
    tableData,
    isLoading,
    isFetching,
    totalPages: apiResponse?.metadata?.total_page || 0,
    currentPage,
    rowsPerPage,
  };
};
